/**
 * @fileoverview Password validation component that displays password strength requirements.
 * Fetches password policy from the server and displays real-time validation feedback.
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import PasswordChecklist from "react-password-checklist";
import { fetchData, intl } from "dmsbasereact";

/**
 * Password Validation component
 *
 * Displays real-time password validation feedback based on configurable password requirements.
 * The component fetches the password policy from a public endpoint and adjusts validation
 * rules accordingly. Works on both authenticated and public pages (e.g., account creation).
 *
 * Supported validation rules:
 * - Minimum length
 * - Uppercase letters
 * - Lowercase letters
 * - Numbers
 * - Special characters
 * - Password confirmation match
 *
 * @param {Object} props - Component props
 * @param {string} props.password - The password value to validate
 * @param {string} props.confirmPassword - The password confirmation value to match against
 * @param {number} [props.length=8] - Minimum password length (default: 8)
 * @returns {JSX.Element} The password validation checklist component
 */
export const PasswordValidation = ({ password, confirmPassword, length = 8, onValidChange }) => {
    // Default rules: MIN_8_CHARACTERS_WITH_UPPER_LOWER_NUMBER_SYMBOL
    const [rules, setRules] = useState([
        "minLength",
        "specialChar",
        "number",
        "capital",
        "lowercase",
        "match"
    ]);

    useEffect(() => {
        const fetchPasswordRules = async () => {
            try {
                // Fetch password settings from public endpoint (no authentication required)
                const response = await fetchData("/login_settings/public", "GET", false);

                if (response.status === 200 && response.data?.data?.password_format_pk) {
                    const passwordFormatPk = response.data.data.password_format_pk;
                    updateRules(passwordFormatPk);
                }
            } catch (error) {
                console.warn("Could not fetch password rules, using default configuration");
                // Keep default rules already set
            }
        };

        fetchPasswordRules();
    }, []);

    const updateRules = (passwordFormatPk) => {
        // PasswordRegexDescription enum values
        const EVERYTHING = 1;
        const UPPER_LOWER_NUMBER_SYMBOL = 2;
        const ONLY_ALPHANUMERIC = 3;
        const MIN_8_CHARACTERS = 4;
        const MIN_8_CHARACTERS_ALPHANUMERIC = 5;
        const MIN_8_CHARACTERS_NUMBER_AND_SYMBOL = 6;
        const MIN_8_CHARACTERS_WITH_UPPER_LOWER_NUMBER_SYMBOL = 7;

        if (passwordFormatPk === EVERYTHING) {
            setRules(["match"]);
        } else if (passwordFormatPk === UPPER_LOWER_NUMBER_SYMBOL) {
            setRules(["specialChar", "number", "capital", "lowercase", "match"]);
        } else if (passwordFormatPk === ONLY_ALPHANUMERIC) {
            setRules(["letter", "match"]);
        } else if (passwordFormatPk === MIN_8_CHARACTERS) {
            setRules(["minLength", "match"]);
        } else if (passwordFormatPk === MIN_8_CHARACTERS_ALPHANUMERIC) {
            setRules(["letter", "minLength", "match"]);
        } else if (passwordFormatPk === MIN_8_CHARACTERS_NUMBER_AND_SYMBOL) {
            setRules(["minLength", "specialChar", "number", "match"]);
        } else if (passwordFormatPk === MIN_8_CHARACTERS_WITH_UPPER_LOWER_NUMBER_SYMBOL) {
            setRules(["minLength", "specialChar", "number", "capital", "lowercase", "match"]);
        }
    };

    const messages = {
        minLength: intl.get("PasswordMinLength", { length }).d(`Password has at least ${length} characters`),
        specialChar: intl.get("PasswordSpecialChar").d("Password has special characters"),
        number: intl.get("PasswordNumber").d("Password has a number"),
        capital: intl.get("PasswordCapital").d("Password has an uppercase letter"),
        lowercase: intl.get("PasswordLowercase").d("Password has a lowercase letter"),
        match: intl.get("PasswordMatch").d("Passwords match"),
        letter: intl.get("PasswordLetter").d("Password has a letter"),
    };

    return (
        <PasswordChecklist
            rules={rules}
            minLength={length}
            value={password}
            valueAgain={confirmPassword}
            messages={messages}
            onChange={onValidChange}
        />
    );
};

PasswordValidation.propTypes = {
    password: PropTypes.string,
    confirmPassword: PropTypes.string,
    length: PropTypes.number,
    onValidChange: PropTypes.func
};
